<template>

  <div v-if="isRecaptchaRendered" v-show="isRecaptchaVisible">
    <vue-recaptcha v-if="isGoogleWebRecaptcha" ref="googleRecaptcha" :sitekey="recaptchaClientKey"
      :loadRecaptchaScript="false" size="invisible" :badge="recaptchaBadgePosition" :theme="theme" @verify="onCaptchaVerified"
      @expired="onCaptchaExpired" @render="onVueRecaptchaRendered" @error="onCaptchaError">
    </vue-recaptcha>

    <app-turnstile-recaptcha v-if="isCloudflareWebRecaptcha" ref="cloudflareRecaptcha" :site-key="recaptchaClientKey"
      :badge="recaptchaBadgePosition" @verify="onCaptchaVerified" @error="onCaptchaError">
    </app-turnstile-recaptcha>
  </div>

</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import AppTurnstileRecaptcha from '@/components/Common/AppTurnstileRecaptcha.vue';

import { logger } from '@/helpers/log.service';
import uiMixin from '@/mixins/ui-mixin';
import { RecaptchaProvider, RecaptchaType } from '@/models/enums';
import { actions as _act } from '@/models/store.actions';
import { isNativeAndroid } from '@/helpers/platform-helper';
import { getLocale } from '@/helpers';
import { messageService } from '@/helpers/message.service';
import { isCancelledError, getCancelledError } from '@/helpers/error-helper';
import { RecaptchaBadgePosition, RecaptchaResult } from '@/models/recaptcha.models';
import { isAuthenticated } from '@/helpers/auth-helper';

const googleRecaptchaUrl = 'https://www.google.com/recaptcha/api.js';
const cloudflareRecaptchaUrl = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export default {
  name: 'AppRecaptcha',
  components: { VueRecaptcha, AppTurnstileRecaptcha },
  mixins: [uiMixin],
  data() {
    return {
      initialized: false,
      isInitializingInProgress: false,
      recaptchaProvider: 0,
      recaptchaType: '',
      recaptchaEnabled: false,
      recaptchaClientKey: undefined,
      recaptchaReadyPromise: null,
      recaptchaReadyResolve: () => {},
      theme: 'light',
      recaptchaGettingResolve: () => {},
      recaptchaGettingReject: () => {},
      googleChallengeClosureObserver: null,
      googleChallengeAppendingObserver: null,
      recaptchaBadgePosition: RecaptchaBadgePosition.BottomLeft
    };
  },
  computed: {
    isGoogleRecaptcha() { return this.recaptchaProvider === RecaptchaProvider.Google; },
    isCloudflareRecaptcha() { return this.recaptchaProvider === RecaptchaProvider.Cloudflare; },
    isWebRecaptcha() { return this.recaptchaType === RecaptchaType.Web; },
    isGoogleWebRecaptcha() { return this.isGoogleRecaptcha && this.isWebRecaptcha; },
    isCloudflareWebRecaptcha() { return this.isCloudflareRecaptcha && this.isWebRecaptcha; },
    isAndroidRecaptcha() { return this.isGoogleRecaptcha && this.recaptchaType === RecaptchaType.Android; },
    isGoogleWebRecaptchaRendered() { return this.recaptchaEnabled && this.isGoogleWebRecaptcha && !!this.recaptchaClientKey; },
    isRecaptchaRendered() { return this.recaptchaEnabled && this.initialized && this.isWebRecaptcha; },
    isRecaptchaVisible() { return this.isRecaptchaRequired; },
    isRecaptchaRequired() { return !isAuthenticated() || (this.$route.meta && this.$route.meta.recaptchaRequired); },
    googleChallengeFrameSelector() { return 'iframe[title*=reCAPTCHA][src*=bframe]'; }
  },
  created() {
    this.initComponent();
  },
  destroyed() {
    this.stopObservers();
    this.$eventBus.$off('user-logged-out', this.initRecaptcha);
    this.$eventBus.$off('recaptcha-token-request', this.onRecaptchaTokenRequest);
  },
  methods: {
    async executeAndroid() {
      logger.debug(`[AppRecaptcha:executeAndroid] verifying android recaptcha`);
      try {
        const token = await window.plugins.recaptchaPlugin.verify({ publicKey: this.recaptchaClientKey });
        logger.debug('[AppRecaptcha:executeAndroid] android recaptcha has verified. token: ', token);
        this.onCaptchaVerified(token);
      } catch (err) {
        logger.warn('[AppRecaptcha:executeAndroid] android recaptcha verification error: ', err);
        this.onCaptchaError(err);
      }
    },
    onCaptchaExpired() {
      logger.debug(`[AppRecaptcha:onCaptchaExpired]`);
      this.$refs.googleRecaptcha.reset();
    },
    onCaptchaVerified(token) {
      logger.debug(`[AppRecaptcha:onCaptchaVerified] token=${token}`);

      if (this.isGoogleWebRecaptchaRendered) this.$refs.googleRecaptcha.reset();

      this.resolveRecaptchaGettingPromise(token);
    },
    onVueRecaptchaRendered(widgetId) {
      logger.debug(`[AppRecaptcha:onVueRecaptchaRendered] widgetId=${widgetId}`);
      this.recaptchaReadyResolve();
    },
    onCaptchaError(err) {
      logger.debug(`[AppRecaptcha:onCaptchaError]`, err);
      this.rejectRecaptchaGettingPromise(err);
    },
    initComponent() {
      logger.debug(`[AppRecaptcha:init]`);

      this.theme = this.$vuetify.theme['isDark'] ? 'dark' : 'light';

      this.$eventBus.$on('recaptcha-token-request', this.onRecaptchaTokenRequest);

      if (this.isRecaptchaRequired) this.initRecaptcha();

      this.$eventBus.$on('user-logged-out', this.initRecaptcha);
    },
    async initRecaptcha() {
      if (this.initialized || this.isInitializingInProgress) return;

      try {
        this.isInitializingInProgress = true;

        this.recaptchaReadyPromise = new Promise(resolve => (this.recaptchaReadyResolve = resolve));

        await this.getRecaptchaSettings();

        logger.debug(`[AppRecaptcha:initRecaptcha] 
recaptchaProvider=${RecaptchaProvider[this.recaptchaProvider]} recaptchaType=${RecaptchaType[this.recaptchaType]}
recaptchaEnabled=${this.recaptchaEnabled} recaptchaClientKey="${this.recaptchaClientKey}"`);

        if (this.recaptchaEnabled && this.isWebRecaptcha) await this.loadProviderRecaptcha();

        this.initialized = true;
        logger.debug(`[AppRecaptcha:initRecaptcha] recaptcha initialized`);

        if (!this.isGoogleWebRecaptcha) this.recaptchaReadyResolve();
      } catch (e) {
        messageService.error({ message: e.message });
        this.recaptchaReadyResolve();
      } finally {
        this.isInitializingInProgress = false;
      }
    },
    async getRecaptchaSettings() {
      try {
        let sett = await this.$store.getters[_act.SETT_GET_STG_WAITER];

        let {
          recaptchaProvider,
          googleReCaptchaEnabled: recaptchaEnabled,
          googleReCaptchaClientKey,
          googleReCaptchaAndroidClientKey,
          cloudflareReCaptchaClientKey,
          useAndroidRecaptcha
        } = sett;

        let recaptchaType = RecaptchaType.Web;
        let clientKey = '';

        if (recaptchaProvider === RecaptchaProvider.Google) {
          if (isNativeAndroid() && useAndroidRecaptcha) {
            recaptchaType = RecaptchaType.Android;
            clientKey = googleReCaptchaAndroidClientKey;
          } else {
            clientKey = googleReCaptchaClientKey;
          }
        } else if (recaptchaProvider === RecaptchaProvider.Cloudflare) {
          clientKey = cloudflareReCaptchaClientKey;
        } else {
          // noinspection ExceptionCaughtLocallyJS
          throw new Error(`Recaptcha provider is not supported`);
        }

        if (recaptchaEnabled && !clientKey) {
          // noinspection ExceptionCaughtLocallyJS
          throw new Error(`Recaptcha client key is not defined`);
        }

        this.recaptchaProvider = recaptchaProvider;
        this.recaptchaType = recaptchaType;
        this.recaptchaEnabled = recaptchaEnabled;
        this.recaptchaClientKey = clientKey;
      } catch (err) {
        logger.error('[AppRecaptcha:getRecaptchaSettings]', err);
        throw new Error(getLocale('appRecaptcha.recaptchaInitializingError').toString());
      }
    },
    loadProviderRecaptcha() {
      return new Promise((resolve, reject) => {
        let currentLang = this.$store.getters[_act.SETT_GET_LOCALEGET];
        let recaptchaUrl;

        switch (this.recaptchaProvider) {
          case RecaptchaProvider.Google:
            recaptchaUrl = googleRecaptchaUrl;
            recaptchaUrl += `?onload=vueRecaptchaApiLoaded&render=explicit&hl=${currentLang}`;
            break;
          case RecaptchaProvider.Cloudflare:
            recaptchaUrl = cloudflareRecaptchaUrl;
            recaptchaUrl += `?render=explicit`;
            break;
          default:
            return reject(new Error(`Recaptcha provider is not supported`));
        }

        logger.debug('[AppRecaptcha:loadProviderRecaptcha] start load recaptcha');

        let captcha = document.createElement('script');
        captcha.setAttribute('src', recaptchaUrl);
        captcha.setAttribute('async', '');
        captcha.setAttribute('defer', '');
        captcha.onload = function() {
          logger.debug('[AppRecaptcha:loadProviderRecaptcha] recaptcha script loaded');
          resolve();
        };
        captcha.onerror = (err) => {
          logger.error('[AppRecaptcha:loadProviderRecaptcha] recaptcha script load error', err);
          reject(new Error(getLocale('appRecaptcha.recaptchaLoadingError').toString()));
        };
        document.head.appendChild(captcha);
      });
    },
    resolveRecaptchaGettingPromise(token) {
      this.stopObservers();
      if (this.recaptchaGettingResolve) {
        this.recaptchaGettingResolve(token);
        this.recaptchaGettingResolve = null;
      }
    },
    rejectRecaptchaGettingPromise(err) {
      this.stopObservers();
      if (this.recaptchaGettingReject) {
        this.recaptchaGettingReject(err);
        this.recaptchaGettingReject = null;
      }
    },
    stopObservers() {
      this.stopGoogleChallengeClosureObserver();
      this.stopGoogleChallengeAppendingObserver();
    },
    stopGoogleChallengeClosureObserver() {
      if (this.googleChallengeClosureObserver) {
        this.googleChallengeClosureObserver.disconnect();
        this.googleChallengeClosureObserver = null;
      }
    },
    stopGoogleChallengeAppendingObserver() {
      if (this.googleChallengeAppendingObserver) {
        this.googleChallengeAppendingObserver.disconnect();
        this.googleChallengeAppendingObserver = null;
      }
    },
    async onRecaptchaTokenRequest() {
      logger.debug(`[AppRecaptcha:onRecaptchaTokenRequest] Got token request`);

      let result = new RecaptchaResult(this.recaptchaType);
      try {
        result.recaptchaToken = await this.getToken();
        result.isSucceeded = true;
      } catch (err) {
        if (isCancelledError(err)) {
          result.isCancelled = true;
        } else {
          result.isError = true;
          result.errorDescription = err.message;
          logger.error('Getting recaptcha token error', err);
        }
      }
      logger.debug(`[AppRecaptcha:onRecaptchaTokenRequest] result`, result);
      this.$eventBus.$emit('recaptcha-token-response', result);
    },
    async getToken() {
      if (this.isRecaptchaRequired) this.initRecaptcha();

      if (this.recaptchaReadyPromise) await this.recaptchaReadyPromise;

      logger.debug(`[AppRecaptcha:getToken] initialized=${this.initialized} recaptchaEnabled=${this.recaptchaEnabled}`);

      if (!this.initialized) throw new Error(getLocale('appRecaptcha.recaptchaIsNotInitialized').toString());

      if (!this.recaptchaEnabled) return '';

      return new Promise((resolve, reject) => {
        this.recaptchaGettingResolve = resolve;
        this.recaptchaGettingReject = reject;

        this.startObserveChallengeClosure();

        if (this.isAndroidRecaptcha) {
          this.executeAndroid();
        } else if (this.isGoogleWebRecaptcha) {
          this.$refs.googleRecaptcha.execute();
        } else if (this.isCloudflareWebRecaptcha) {
          this.$refs.cloudflareRecaptcha.execute();
        }
      });
    },
    startObserveChallengeClosure() {
      if (!this.isGoogleRecaptcha) return;

      let challengeFrame = document.querySelectorAll(this.googleChallengeFrameSelector)[0];

      if (challengeFrame) {
        this.createChallengeFrameHidingObserver(challengeFrame);
      } else {
        this.createChallengeFrameAppendingObserver();
      }
    },
    createChallengeFrameHidingObserver(challengeFrame) {
      let frameContainer = challengeFrame.parentElement && challengeFrame.parentElement.parentElement
        ? challengeFrame.parentElement.parentElement
        : undefined;

      if (!frameContainer) return;

      this.googleChallengeClosureObserver = new MutationObserver(() => {
        let st = getComputedStyle(frameContainer, null);
        let vis = st.getPropertyValue('visibility');
        if (vis === 'hidden') {
          logger.debug(`[AppRecaptcha:GoogleChallengeClosureObserver] challenge closed`);
          this.rejectRecaptchaGettingPromise(getCancelledError());
        }
      });

      this.googleChallengeClosureObserver.observe(frameContainer, { attributes: true, attributeFilter: ['style'] });
    },
    createChallengeFrameAppendingObserver() {
      const targetElement = document.body;

      this.googleChallengeAppendingObserver = new MutationObserver((mutationRecords) => {
        mutationRecords.forEach((mutationRecord) => {
          if (mutationRecord.addedNodes.length) {
            const reCaptchaParentContainer = mutationRecord.addedNodes[0];
            const challengeFrame = reCaptchaParentContainer.querySelectorAll(this.googleChallengeFrameSelector)[0];
            if (challengeFrame) {
              logger.debug(`[AppRecaptcha:GoogleChallengeAppendingObserver] challenge frame added to the DOM`, challengeFrame);
              this.createChallengeFrameHidingObserver(challengeFrame);
              this.stopGoogleChallengeAppendingObserver();
            }
          }
        });
      });

      this.googleChallengeAppendingObserver.observe(targetElement, { childList: true });
    }
  },
  watch: {
    $route(to) {
      if (!this.initialized && to.meta && to.meta.recaptchaRequired) {
        this.initRecaptcha();
      }
    }
  }
};
</script>
